import React, {useEffect} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../_metronic/helpers'
import {MenuComponent} from '../../../_metronic/assets/ts/components'
import {useAdminNotificationPool} from '../../config/admin-notification.init'
import {AdminNotification} from '../../types/admin-notification.type'
import {NotificationItem} from './NotificationItem'
import {GhostUnreadEmpty} from './GhostUnreadEmpty'
import {getNotificationLink} from './notification-links'

const btnClass =
  'btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px w-md-40px h-md-40px'

const NotificationBell: React.FC = () => {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    countsByType,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useAdminNotificationPool()

  // The dropdown renders its list asynchronously, so Metronic needs to
  // re-attach its menu handlers once items land.
  useEffect(() => {
    MenuComponent.reinitialization()
  }, [notifications.length, unreadCount, loading])

  const handleSelect = async (notification: AdminNotification) => {
    await markAsRead(notification._id)
    const link = getNotificationLink(notification)
    if (link) navigate(link)
  }

  const recent = notifications.slice(0, 8)
  const ghostUnread = !loading && recent.length === 0 && unreadCount > 0

  return (
    <div className='app-navbar-item ms-1 ms-lg-3'>
      <div
        data-kt-menu-trigger="{default: 'click'}"
        data-kt-menu-attach='parent'
        data-kt-menu-placement='bottom-end'
        className={clsx('position-relative', btnClass)}
        title='Notifications'
      >
        <KTSVG path='/media/icons/duotune/general/gen007.svg' className='svg-icon-1' />
        {unreadCount > 0 && (
          <span className='position-absolute top-0 start-100 translate-middle badge badge-circle badge-danger fs-9'>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>

      <div
        className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
        data-kt-menu='true'
      >
        <div className='d-flex align-items-center justify-content-between px-6 py-4 border-bottom border-gray-200'>
          <h3 className='fw-bold text-gray-800 fs-5 m-0'>
            Notifications
            {unreadCount > 0 && (
              <span className='fs-8 fw-semibold text-muted ps-2'>{unreadCount} unread</span>
            )}
          </h3>
          {unreadCount > 0 && (
            <button
              type='button'
              className='btn btn-sm btn-light-primary py-1 px-3 fs-8'
              onClick={() => markAllAsRead()}
            >
              Mark all read
            </button>
          )}
        </div>

        {error && (
          <div className='alert alert-danger d-flex align-items-center m-4 p-3 fs-8'>
            <span>{error}</span>
          </div>
        )}

        <div className='scroll-y mh-400px'>
          {loading && recent.length === 0 && (
            <div className='text-center text-muted py-10 fs-7'>Loading notifications...</div>
          )}

          {ghostUnread && (
            <GhostUnreadEmpty
              compact
              unreadCount={unreadCount}
              countsByType={countsByType}
              onMarkAllAsRead={() => void markAllAsRead()}
              onRefresh={() => refresh()}
            />
          )}

          {!loading && recent.length === 0 && unreadCount === 0 && (
            <div className='text-center text-muted py-10 fs-7'>You are all caught up.</div>
          )}

          {recent.map((notification) => (
            <NotificationItem
              key={notification._id}
              notification={notification}
              onSelect={handleSelect}
              compact
            />
          ))}
        </div>

        <div className='py-3 text-center border-top border-gray-200'>
          <button
            type='button'
            className='btn btn-sm btn-link text-primary fw-semibold'
            onClick={() => navigate('/notifications')}
          >
            View all notifications
          </button>
        </div>
      </div>
    </div>
  )
}

export {NotificationBell}
